import { useState, useEffect } from 'react';
import { Download, Search, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

/**
 * DataTables component for Admin dashboard
 * Features: Search, date filters, pagination, CSV export
 */

// Helper to format IST
const formatIST = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

// Get last export timestamp from localStorage
const getLastExport = (formType) => {
  const stored = localStorage.getItem(`lastExport_${formType}`);
  return stored ? new Date(stored) : null;
};

// Set last export timestamp
const setLastExport = (formType) => {
  localStorage.setItem(`lastExport_${formType}`, new Date().toISOString());
};

// CSV Export helper
const exportToCSV = (data, filename, columns) => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }
  
  const headers = columns.map(c => c.label).join(',');
  const rows = data.map(row => 
    columns.map(c => {
      let val = c.accessor(row);
      if (val === null || val === undefined) val = '';
      val = String(val);
      if (/^[=+\-@\t\r]/.test(val)) val = `'${val}`;
      val = val.replace(/"/g, '""');
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        val = `"${val}"`;
      }
      return val;
    }).join(',')
  ).join('\n');
  
  const csv = `${headers}\n${rows}`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};

// Reusable Table Component
const DataTable = ({ data, columns, title, formType, emptyMessage }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [lastExportTime, setLastExportTime] = useState(getLastExport(formType));
  
  const pageSize = 20;

  useEffect(() => {
    setLastExportTime(getLastExport(formType));
  }, [formType]);

  // Filter data
  const filteredData = data.filter(row => {
    if (search) {
      const searchLower = search.toLowerCase();
      const matchesSearch = columns.some(col => {
        const val = col.accessor(row);
        return val && String(val).toLowerCase().includes(searchLower);
      });
      if (!matchesSearch) return false;
    }
    
    const rowDate = new Date(row.submitted_at || row.created_at);
    if (dateFrom && rowDate < new Date(dateFrom)) return false;
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (rowDate > toDate) return false;
    }
    
    return true;
  });

  // New records since last export
  const newRecords = lastExportTime 
    ? data.filter(row => new Date(row.submitted_at || row.created_at) > lastExportTime)
    : data;
  
  // Pagination
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = filteredData.slice((page - 1) * pageSize, page * pageSize);

  const handleExport = (exportData, type) => {
    exportToCSV(exportData, `${formType}_${type}`, columns);
    if (type === 'new' || type === 'all') {
      setLastExport(formType);
      setLastExportTime(new Date());
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-100">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-500">
              {filteredData.length} records 
              {newRecords.length > 0 && newRecords.length !== data.length && (
                <span className="text-teal-600 ml-2">• {newRecords.length} new since last export</span>
              )}
            </p>
            {lastExportTime && (
              <p className="text-xs text-slate-400 mt-1">
                Last export: {formatIST(lastExportTime.toISOString())}
              </p>
            )}
          </div>
          
          {/* Search and Date Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9 w-40"
              />
            </div>
            <div className="flex items-center gap-1">
              <Calendar size={14} className="text-slate-400" />
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="w-32 text-xs"
              />
              <span className="text-slate-400">-</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="w-32 text-xs"
              />
            </div>
          </div>
        </div>
        
        {/* Export Buttons */}
        <div className="flex flex-wrap items-center gap-2 mt-4">
          {/* Export New Only */}
          {newRecords.length > 0 && newRecords.length !== data.length && (
            <Button
              variant="default"
              size="sm"
              onClick={() => handleExport(newRecords, 'new')}
              className="bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-2"
            >
              <Download size={14} />
              Export New ({newRecords.length})
            </Button>
          )}
          
          {/* Export Filtered */}
          {(dateFrom || dateTo || search) && filteredData.length !== data.length && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport(filteredData, 'filtered')}
              className="flex items-center gap-2"
            >
              <Download size={14} />
              Export Filtered ({filteredData.length})
            </Button>
          )}
          
          {/* Export All */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport(data, 'all')}
            className="flex items-center gap-2"
          >
            <Download size={14} />
            Export All ({data.length})
          </Button>
        </div>
      </div>
      
      {/* Table */}
      <div className="overflow-x-auto">
        {paginatedData.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            {emptyMessage || 'No data available'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                {columns.map((col, idx) => (
                  <th 
                    key={idx} 
                    className="px-4 py-3 text-left font-medium text-slate-600 whitespace-nowrap"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedData.map((row, rowIdx) => {
                const isNew = lastExportTime && new Date(row.submitted_at || row.created_at) > lastExportTime;
                return (
                  <tr key={rowIdx} className={`hover:bg-slate-50 transition-colors ${isNew ? 'bg-teal-50/30' : ''}`}>
                    {columns.map((col, colIdx) => (
                      <td 
                        key={colIdx} 
                        className={`px-4 py-3 ${col.className || ''} ${col.wrap ? '' : 'whitespace-nowrap'}`}
                      >
                        {col.render ? col.render(row) : col.accessor(row) || '-'}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-slate-100 flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, filteredData.length)} of {filteredData.length}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              <ChevronLeft size={16} />
            </Button>
            <span className="text-sm text-slate-600 px-2">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Career Readiness Table
export const CareerReadinessTable = ({ data }) => {
  const columns = [
    { label: 'Date (IST)', accessor: (r) => formatIST(r.submitted_at) },
    { label: 'Name', accessor: (r) => r.firstName },
    { label: 'Email', accessor: (r) => r.email },
    { label: 'WhatsApp', accessor: (r) => r.whatsapp },
    { label: 'Stage', accessor: (r) => r.stage },
    { label: 'Qualification', accessor: (r) => r.qualification },
    { label: 'Target Role', accessor: (r) => r.targetRole, wrap: true },
    { 
      label: 'Overall Score', 
      accessor: (r) => {
        if (!r.scores) return '-';
        return Math.round((
          (r.scores.career_direction || 0) +
          (r.scores.profile_strength || 0) +
          (r.scores.interview_readiness || 0) +
          (r.scores.job_search || 0)
        ) / 4);
      },
      render: (r) => {
        if (!r.scores) return '-';
        const avg = Math.round((
          (r.scores.career_direction || 0) +
          (r.scores.profile_strength || 0) +
          (r.scores.interview_readiness || 0) +
          (r.scores.job_search || 0)
        ) / 4);
        const color = avg >= 75 ? 'text-green-600' : avg >= 50 ? 'text-amber-600' : 'text-red-600';
        return <span className={`font-medium ${color}`}>{avg}</span>;
      }
    },
    { 
      label: 'Readiness Level', 
      accessor: (r) => {
        if (!r.scores) return '-';
        const avg = Math.round((
          (r.scores.career_direction || 0) +
          (r.scores.profile_strength || 0) +
          (r.scores.interview_readiness || 0) +
          (r.scores.job_search || 0)
        ) / 4);
        return avg >= 75 ? 'Strong' : avg >= 50 ? 'Moderate' : 'Needs Work';
      },
      render: (r) => {
        if (!r.scores) return '-';
        const avg = Math.round((
          (r.scores.career_direction || 0) +
          (r.scores.profile_strength || 0) +
          (r.scores.interview_readiness || 0) +
          (r.scores.job_search || 0)
        ) / 4);
        const level = avg >= 75 ? 'Strong' : avg >= 50 ? 'Moderate' : 'Needs Work';
        const bgColor = avg >= 75 ? 'bg-green-100 text-green-700' : avg >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
        return <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor}`}>{level}</span>;
      }
    },
    { label: 'Career Direction', accessor: (r) => r.scores?.career_direction || '-' },
    { label: 'Profile Strength', accessor: (r) => r.scores?.profile_strength || '-' },
    { label: 'Interview Readiness', accessor: (r) => r.scores?.interview_readiness || '-' },
    { label: 'Job Search', accessor: (r) => r.scores?.job_search || '-' },
    { 
      label: 'Priority Area', 
      accessor: (r) => {
        const labels = {
          'career_direction': 'Career Direction',
          'profile_strength': 'Profile Strength',
          'interview_readiness': 'Interview Readiness',
          'job_search': 'Job Search'
        };
        return labels[r.priorityArea] || r.priorityArea || '-';
      }
    },
    { label: 'UTM Source', accessor: (r) => r.utmParams?.utm_source || '-' },
    { label: 'UTM Medium', accessor: (r) => r.utmParams?.utm_medium || '-' },
    { label: 'UTM Campaign', accessor: (r) => r.utmParams?.utm_campaign || '-' },
  ];
  
  return (
    <DataTable
      data={data}
      columns={columns}
      title="Career Readiness Check Submissions"
      formType="career_readiness"
      emptyMessage="No career readiness check submissions yet"
    />
  );
};

// Applications Table
export const ApplicationsTable = ({ data }) => {
  const columns = [
    { label: 'Date (IST)', accessor: (r) => formatIST(r.submitted_at) },
    { label: 'Name', accessor: (r) => r.full_name },
    { label: 'Email', accessor: (r) => r.email },
    { label: 'Phone', accessor: (r) => r.phone },
    { label: 'City', accessor: (r) => r.city },
    { label: 'College', accessor: (r) => r.college_name, wrap: true },
    { label: 'Degree', accessor: (r) => r.degree_stream },
    { label: 'Status', accessor: (r) => r.current_status },
    { 
      label: 'Plan', 
      accessor: (r) => {
        const labels = { 'clarity-session': 'Clarity', 'career-blueprint': 'Blueprint', 'career-readiness': 'Readiness' };
        return labels[r.interested_plan] || r.interested_plan;
      },
      render: (r) => {
        const planColors = {
          'clarity-session': 'bg-amber-100 text-amber-700',
          'career-blueprint': 'bg-teal-100 text-teal-700',
          'career-readiness': 'bg-emerald-100 text-emerald-700'
        };
        const labels = { 'clarity-session': 'Clarity', 'career-blueprint': 'Blueprint', 'career-readiness': 'Readiness' };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${planColors[r.interested_plan] || 'bg-slate-100'}`}>
            {labels[r.interested_plan] || r.interested_plan}
          </span>
        );
      }
    },
    { label: 'Career Clarity', accessor: (r) => r.career_clarity, wrap: true },
    { label: 'Target Role', accessor: (r) => r.target_role, wrap: true },
    { label: 'Biggest Challenge', accessor: (r) => r.biggest_challenge, wrap: true, className: 'max-w-xs truncate' },
    { label: 'Why Join', accessor: (r) => r.why_join, wrap: true, className: 'max-w-xs truncate' },
    { label: 'Can Commit', accessor: (r) => r.can_commit_time },
    { label: 'Call Time', accessor: (r) => r.screening_call_time },
    { 
      label: 'Payment', 
      accessor: (r) => r.payment_status || 'pending',
      render: (r) => {
        const status = r.payment_status || 'pending';
        const color = status === 'paid' ? 'text-green-600' : 'text-slate-500';
        return <span className={`font-medium ${color}`}>{status}</span>;
      }
    },
  ];
  
  return (
    <DataTable
      data={data}
      columns={columns}
      title="Program Applications"
      formType="applications"
      emptyMessage="No applications yet"
    />
  );
};

// Consultations Table
export const ConsultationsTable = ({ data }) => {
  const columns = [
    { label: 'Date (IST)', accessor: (r) => formatIST(r.submitted_at) },
    { label: 'Name', accessor: (r) => r.full_name },
    { label: 'Email', accessor: (r) => r.email },
    { label: 'Phone', accessor: (r) => r.phone },
    { label: 'Current Situation', accessor: (r) => r.current_situation, wrap: true },
    { label: 'Preferred Time', accessor: (r) => r.preferred_time },
    { label: 'Questions', accessor: (r) => r.questions, wrap: true, className: 'max-w-xs truncate' },
    { label: 'Resume', accessor: (r) => r.resume_filename ? 'Yes' : 'No' },
    { 
      label: 'Status', 
      accessor: (r) => r.status || 'pending',
      render: (r) => {
        const status = r.status || 'pending';
        const color = status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700';
        return <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>{status}</span>;
      }
    },
  ];
  
  return (
    <DataTable
      data={data}
      columns={columns}
      title="Consultation Requests"
      formType="consultations"
      emptyMessage="No consultation requests yet"
    />
  );
};

// Talent Network Table
export const TalentNetworkTable = ({ data }) => {
  const columns = [
    { label: 'Date (IST)', accessor: (r) => formatIST(r.submitted_at) },
    { label: 'Name', accessor: (r) => r.full_name },
    { label: 'Email', accessor: (r) => r.email },
    { label: 'Phone', accessor: (r) => r.phone },
    { label: 'City', accessor: (r) => r.city },
    { label: 'Current Status', accessor: (r) => r.current_status },
    { label: 'Graduation Year', accessor: (r) => r.graduation_year },
    { label: 'Degree', accessor: (r) => r.degree },
    { label: 'College', accessor: (r) => r.college, wrap: true },
    { label: 'Skills', accessor: (r) => r.skills, wrap: true, className: 'max-w-xs truncate' },
    { label: 'Work Experience', accessor: (r) => r.work_experience },
    { label: 'LinkedIn', accessor: (r) => r.linkedin_url ? 'Yes' : 'No' },
    { label: 'UTM Source', accessor: (r) => r.utm_source },
    { label: 'UTM Medium', accessor: (r) => r.utm_medium },
    { label: 'UTM Campaign', accessor: (r) => r.utm_campaign },
  ];
  
  return (
    <DataTable
      data={data}
      columns={columns}
      title="Talent Network Members"
      formType="talent_network"
      emptyMessage="No talent network members yet"
    />
  );
};

// College Inquiries Table
export const CollegeInquiriesTable = ({ data }) => {
  const columns = [
    { label: 'Date (IST)', accessor: (r) => formatIST(r.submitted_at) },
    { label: 'Institution', accessor: (r) => r.institution_name },
    { label: 'Contact Name', accessor: (r) => r.contact_name },
    { label: 'Email', accessor: (r) => r.email },
    { label: 'Phone', accessor: (r) => r.phone },
    { label: 'Role', accessor: (r) => r.role },
    { label: 'Student Count', accessor: (r) => r.student_count },
    { label: 'Interests', accessor: (r) => r.interests?.join(', '), wrap: true },
    { label: 'Message', accessor: (r) => r.message, wrap: true, className: 'max-w-xs truncate' },
  ];
  
  return (
    <DataTable
      data={data}
      columns={columns}
      title="College Partnership Inquiries"
      formType="college_inquiries"
      emptyMessage="No college inquiries yet"
    />
  );
};

// Employer Inquiries Table
export const EmployerInquiriesTable = ({ data }) => {
  const columns = [
    { label: 'Date (IST)', accessor: (r) => formatIST(r.submitted_at) },
    { label: 'Company', accessor: (r) => r.company_name },
    { label: 'Contact Name', accessor: (r) => r.contact_name },
    { label: 'Email', accessor: (r) => r.email },
    { label: 'Phone', accessor: (r) => r.phone },
    { label: 'Role', accessor: (r) => r.role },
    { label: 'Hiring Roles', accessor: (r) => r.hiring_roles, wrap: true },
    { label: 'Hiring Count', accessor: (r) => r.hiring_count },
    { label: 'Timeline', accessor: (r) => r.timeline },
    { label: 'Message', accessor: (r) => r.message, wrap: true, className: 'max-w-xs truncate' },
  ];
  
  return (
    <DataTable
      data={data}
      columns={columns}
      title="Employer Inquiries"
      formType="employer_inquiries"
      emptyMessage="No employer inquiries yet"
    />
  );
};

// Main DataTables component
export const AdminDataTables = ({ 
  careerReadinessLeads, 
  applications, 
  consultations, 
  networkMembers,
  collegeInquiries,
  employerInquiries 
}) => {
  const [subTab, setSubTab] = useState('career_readiness');
  
  const subTabs = [
    { id: 'career_readiness', label: 'Career Readiness', count: careerReadinessLeads?.length || 0 },
    { id: 'applications', label: 'Applications', count: applications?.length || 0 },
    { id: 'consultations', label: 'Consultations', count: consultations?.length || 0 },
    { id: 'network', label: 'Talent Network', count: networkMembers?.length || 0 },
    { id: 'colleges', label: 'Colleges', count: collegeInquiries?.length || 0 },
    { id: 'employers', label: 'Employers', count: employerInquiries?.length || 0 },
  ];
  
  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2 bg-white p-2 rounded-xl">
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setSubTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              subTab === tab.id 
                ? 'bg-slate-900 text-white' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>
      
      {/* Tables */}
      {subTab === 'career_readiness' && <CareerReadinessTable data={careerReadinessLeads || []} />}
      {subTab === 'applications' && <ApplicationsTable data={applications || []} />}
      {subTab === 'consultations' && <ConsultationsTable data={consultations || []} />}
      {subTab === 'network' && <TalentNetworkTable data={networkMembers || []} />}
      {subTab === 'colleges' && <CollegeInquiriesTable data={collegeInquiries || []} />}
      {subTab === 'employers' && <EmployerInquiriesTable data={employerInquiries || []} />}
    </div>
  );
};

export default AdminDataTables;
